import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatRelativeTime, resolvePresenceTimestamp, isOnlineRecently } from '../../common/timeAgo';
import PersonAvatar, { buildPersonAvatarUrl, getPersonInitial } from '../../common/avatar/PersonAvatar';
import '../../../css/admin/artists/ArtistBubbleView.css';
import ArtistCareerTimelineIcon from './ArtistCareerTimelineIcon';
import ArtistPartnerBadge from './ArtistPartnerBadge';
import ArtistAiAnalysisBadge from './ArtistAiAnalysisBadge';
import {
  BUBBLE_SIZES,
  CONNECTION_DISTANCE,
  MIN_SIZE,
  SNS_ITEMS,
  SnsIcons,
  calcAge,
  computeImportance,
  firstNonEmpty,
  getBadgeLabel,
  getCachedImage,
  getLayoutConfig,
  renderGenderJP,
  resolveArtistEmail,
  resolveTierIndex,
  resolveViewportWidth,
} from './artistBubbleViewHelpers';

// アバターカラーのルール（PersonAvatar と同じ gender ベース）。
const genderKeyOf = (artist) => {
  switch (String(artist?.gender || '').trim().toLowerCase()) {
    case 'male':
    case 'man':
    case 'm':
    case '男性':
    case '男':
      return 'male';
    case 'female':
    case 'woman':
    case 'f':
    case '女性':
    case '女':
      return 'female';
    default:
      return 'unknown';
  }
};

// DOM 表示用（CSS が var() を解決する）。
const AVATAR_BG_VAR = {
  male: 'var(--person-avatar-artist-male-bg)',
  female: 'var(--person-avatar-artist-female-bg)',
  unknown: 'var(--person-avatar-artist-unknown-bg)',
};

// canvas は var() を解釈できないため、解決に失敗したときの確定色（tokens.css と同値）。
const AVATAR_BG_FALLBACK = {
  male: '#3b82f6',
  female: '#ec4899',
  unknown: '#f97316',
};

const fallbackColorFor = (artist) => AVATAR_BG_VAR[genderKeyOf(artist)];

function ArtistBubbleView({
  artists = [],
  partByValue,
  groupTagById,
  onOpenDetail,
  showInlineCard = true,
  onSelectArtist = null,
  getSizeBoost = null,
  cardVariant = 'list',
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [activeArtist, setActiveArtist] = useState(null);
  const activeArtistRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(() => resolveViewportWidth());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [canFullscreen, setCanFullscreen] = useState(false);
  const [hideFullscreenControl, setHideFullscreenControl] = useState(false);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportWidth(resolveViewportWidth());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setCanFullscreen(Boolean(document.fullscreenEnabled));
    const handleFullscreenChange = () => {
      const target = containerRef.current;
      const active = document.fullscreenElement;
      const isActive = !!(target && active && (active === target || target.contains(active)));
      setIsFullscreen(isActive);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const target = containerRef.current;
    if (!isFullscreen || !target) {
      setHideFullscreenControl(false);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      return undefined;
    }
    const scheduleHide = () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        setHideFullscreenControl(true);
      }, 1000);
    };
    const handleMouseMove = () => {
      setHideFullscreenControl(false);
      scheduleHide();
    };
    const handleMouseLeave = () => {
      setHideFullscreenControl(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
    const handleMouseEnter = () => {
      setHideFullscreenControl(false);
      scheduleHide();
    };

    scheduleHide();
    target.addEventListener('mousemove', handleMouseMove);
    target.addEventListener('mouseleave', handleMouseLeave);
    target.addEventListener('mouseenter', handleMouseEnter);
    return () => {
      target.removeEventListener('mousemove', handleMouseMove);
      target.removeEventListener('mouseleave', handleMouseLeave);
      target.removeEventListener('mouseenter', handleMouseEnter);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [isFullscreen]);

  const layout = useMemo(
    () => getLayoutConfig(artists.length, viewportWidth),
    [artists.length, viewportWidth]
  );
  const containerStyle = useMemo(
    () => ({
      minHeight: `${layout.minHeight}px`,
      height: `clamp(${layout.minHeight}px, ${layout.ratio}vh, ${layout.maxHeight}px)`,
    }),
    [layout]
  );
  const resolvedContainerStyle = useMemo(
    () => (isFullscreen
      ? { ...containerStyle, minHeight: '100%', height: '100%' }
      : containerStyle),
    [containerStyle, isFullscreen]
  );

  const normalizedArtists = useMemo(() => {
    const total = artists.length;
    return artists.map((artist, index) => ({
      artist,
      importance: computeImportance(index, total),
    }));
  }, [artists]);
  const hasConcertmaster = useMemo(
    () => artists.some((artist) => artist?.is_concertmaster),
    [artists]
  );

  useEffect(() => {
    if (!activeArtist) return;
    const stillExists = artists.some((artist) => artist.artist_id === activeArtist.artist_id);
    if (!stillExists) setActiveArtist(null);
  }, [artists, activeArtist]);

  useEffect(() => {
    activeArtistRef.current = activeArtist;
  }, [activeArtist]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    // canvas は var() を解釈できないため、アバター色トークンを具体色へ解決してから塗る。
    const readCssVar = (name) => {
      try {
        const value = getComputedStyle(container).getPropertyValue(name).trim();
        return value || null;
      } catch {
        return null;
      }
    };
    const avatarBgByGender = {
      male: readCssVar('--person-avatar-artist-male-bg') || AVATAR_BG_FALLBACK.male,
      female: readCssVar('--person-avatar-artist-female-bg') || AVATAR_BG_FALLBACK.female,
      unknown: readCssVar('--person-avatar-artist-unknown-bg') || AVATAR_BG_FALLBACK.unknown,
    };

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let width = 0;
    let height = 0;
    let particles = [];
    const mouse = { x: -9999, y: -9999 };
    const bubbleSizes = layout.bubbleSizes || {
      small: MIN_SIZE,
      medium: BUBBLE_SIZES.medium,
      large: BUBBLE_SIZES.large,
      xlarge: layout.maxSize,
    };
    const sizeByTier = [
      bubbleSizes.small,
      bubbleSizes.medium,
      bubbleSizes.large,
      bubbleSizes.xlarge,
    ];
    const variantByTier = ['proxy', 'proxy', 'medium', 'medium'];
    const maxRadius = sizeByTier[3] / 2;

    class Particle {
      constructor(entry, containerWidth, containerHeight) {
        const { artist, importance } = entry;
        this.artist = artist;
        this.importance = importance;
        this.hasProfile = Boolean(artist.profile_picture);
        this.initial = getPersonInitial(artist.name);
        this.fallbackColor = avatarBgByGender[genderKeyOf(artist)] || AVATAR_BG_FALLBACK.unknown;

        const centerX = containerWidth / 2;
        const centerY = containerHeight / 2;
        const spread = Math.min(containerWidth, containerHeight) * 0.45;
        const angle = Math.random() * Math.PI * 2;
        const distance = (1 - Math.sqrt(importance)) * spread + (Math.random() * 40 - 20);

        this.x = centerX + Math.cos(angle) * distance;
        this.y = centerY + Math.sin(angle) * distance;
        this.homeX = this.x;
        this.homeY = this.y;

        const baseTier = resolveTierIndex(importance);
        let tierIndex = baseTier;
        const sizeBoost = typeof getSizeBoost === 'function' ? Math.max(0, getSizeBoost(artist)) : 0;
        if (sizeBoost >= 2) {
          tierIndex = 3;
        } else if (sizeBoost >= 1.5) {
          tierIndex = Math.max(tierIndex, 2);
        } else if (sizeBoost > 0) {
          tierIndex = Math.min(3, tierIndex + 1);
        }
        if (hasConcertmaster && sizeBoost < 2) {
          tierIndex = Math.min(tierIndex, 2);
        }
        this.baseRadius = (sizeByTier[tierIndex] || sizeByTier[0]) / 2;
        this.radius = this.baseRadius;
        this.targetRadius = this.baseRadius;

        this.vx = (Math.random() - 0.5) * 0.35;
        this.vy = (Math.random() - 0.5) * 0.35;

        const variant = variantByTier[tierIndex] || 'proxy';
        this.imageUrl = this.hasProfile
          ? buildPersonAvatarUrl({ role: 'artist', profilePicture: artist.profile_picture, variant })
          : '';
        this.image = null;
        this.isLoaded = false;
        if (this.imageUrl) {
          const cached = getCachedImage(this.imageUrl, () => {
            this.isLoaded = true;
          });
          this.image = cached.image;
          this.isLoaded = cached.isLoaded;
        }

        this.angleOffset = Math.random() * Math.PI * 2;
        this.angleSpeed = 0.002 + Math.random() * 0.004;
      }

      update(pointer, allParticles) {
        this.angleOffset += this.angleSpeed;
        this.x += this.vx + Math.cos(this.angleOffset) * 0.18;
        this.y += this.vy + Math.sin(this.angleOffset) * 0.18;

        const centerX = width / 2;
        const centerY = height / 2;
        const centerPullFactor = 0.00001 + (this.importance * 0.00003);
        this.vx += (centerX - this.x) * centerPullFactor;
        this.vy += (centerY - this.y) * centerPullFactor;

        const dxCenter = this.x - centerX;
        const dyCenter = this.y - centerY;
        const distFromCenter = Math.sqrt(dxCenter * dxCenter + dyCenter * dyCenter);
        const maxDist = Math.min(width, height) * 0.62;
        if (distFromCenter > maxDist) {
          const angleToCenter = Math.atan2(dyCenter, dxCenter);
          this.vx -= Math.cos(angleToCenter) * 0.02;
          this.vy -= Math.sin(angleToCenter) * 0.02;
        }

        const padding = this.radius + 14;
        const wallForce = 0.004;
        if (this.x < padding) this.vx += (padding - this.x) * wallForce;
        if (this.x > width - padding) this.vx -= (this.x - (width - padding)) * wallForce;
        if (this.y < padding) this.vy += (padding - this.y) * wallForce;
        if (this.y > height - padding) this.vy -= (this.y - (height - padding)) * wallForce;

        const dx = pointer.x - this.x;
        const dy = pointer.y - this.y;
        const distToMouse = Math.sqrt(dx * dx + dy * dy);
        const isHovered = distToMouse < (this.radius + 6);
        if (isHovered) {
          this.targetRadius = Math.min(this.baseRadius * 1.35, maxRadius);
          this.vx *= 0.88;
          this.vy *= 0.88;
          this.x += dx * 0.02;
          this.y += dy * 0.02;
        } else {
          this.targetRadius = this.baseRadius;
          this.vx *= 0.94;
          this.vy *= 0.94;
        }
        this.radius += (this.targetRadius - this.radius) * 0.1;
        if (this.radius < 1) this.radius = 1;

        allParticles.forEach((other) => {
          if (other === this) return;
          const idx = other.x - this.x;
          const idy = other.y - this.y;
          const distSq = idx * idx + idy * idy;
          const minDist = this.radius + other.radius + 10;
          const minDistSq = minDist * minDist;
          if (distSq < minDistSq) {
            const dist = Math.sqrt(distSq) || 0.1;
            const overlap = minDist - dist;
            const force = overlap * 0.0045;
            const angle = Math.atan2(idy, idx);
            this.vx -= Math.cos(angle) * force;
            this.vy -= Math.sin(angle) * force;
          }
        });

        const maxSpeed = 3;
        const currentSpeed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (currentSpeed > maxSpeed) {
          this.vx = (this.vx / currentSpeed) * maxSpeed;
          this.vy = (this.vy / currentSpeed) * maxSpeed;
        }

        if (
          Number.isNaN(this.x) ||
          Number.isNaN(this.y) ||
          Number.isNaN(this.vx) ||
          Number.isNaN(this.vy) ||
          Number.isNaN(this.radius)
        ) {
          this.x = this.homeX;
          this.y = this.homeY;
          this.vx = 0;
          this.vy = 0;
          this.radius = this.baseRadius;
        }
      }

      draw(ctx) {
        ctx.save();
        ctx.shadowBlur = this.radius * 0.35;
        ctx.shadowColor = 'rgba(15, 23, 42, 0.2)';

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        ctx.fillStyle = this.fallbackColor;
        ctx.fill();

        if (this.image && this.isLoaded) {
          const size = this.radius * 2;
          ctx.drawImage(this.image, this.x - this.radius, this.y - this.radius, size, size);
        } else if (!this.hasProfile) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
          ctx.font = `600 ${Math.max(12, this.radius * 0.7)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(this.initial, this.x, this.y);
        }

        ctx.restore();

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }

    const resizeCanvas = (nextWidth, nextHeight) => {
      width = nextWidth;
      height = nextHeight;
      const scale = window.devicePixelRatio || 1;
      canvas.width = width * scale;
      canvas.height = height * scale;
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
    };

    const initParticles = () => {
      particles = normalizedArtists.map((entry) => new Particle(entry, width, height));
    };

    let resizeObserver;
    let resizeRafId = null;
    let lastWidth = 0;
    let lastHeight = 0;

    const applyResize = () => {
      const nextWidth = container.clientWidth;
      const nextHeight = container.clientHeight;
      if (nextWidth === lastWidth && nextHeight === lastHeight) return;
      lastWidth = nextWidth;
      lastHeight = nextHeight;
      resizeCanvas(nextWidth, nextHeight);
      initParticles();
    };

    const scheduleResize = () => {
      if (resizeRafId) return;
      resizeRafId = requestAnimationFrame(() => {
        resizeRafId = null;
        applyResize();
      });
    };

    const handleResize = () => scheduleResize();

    const getPointerPosition = (event) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    const pickParticle = (x, y) => {
      let clicked = null;
      let minDistance = Infinity;
      particles.forEach((p) => {
        const dx = p.x - x;
        const dy = p.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < p.radius && dist < minDistance) {
          minDistance = dist;
          clicked = p;
        }
      });
      return clicked;
    };

    const handleMouseMove = (event) => {
      const pos = getPointerPosition(event);
      mouse.x = pos.x;
      mouse.y = pos.y;
    };

    const handleMouseLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    const handleClick = (event) => {
      const pos = getPointerPosition(event);
      const clickedParticle = pickParticle(pos.x, pos.y);
      if (clickedParticle) {
        setActiveArtist(clickedParticle.artist);
        if (onSelectArtist) onSelectArtist(clickedParticle.artist);
        particles.forEach((p) => {
          if (p === clickedParticle) return;
          const dx = p.x - pos.x;
          const dy = p.y - pos.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 280) {
            const angle = Math.atan2(dy, dx);
            const force = 1.3 * (1 - dist / 280);
            p.vx += Math.cos(angle) * force;
            p.vy += Math.sin(angle) * force;
          }
        });
      } else {
        setActiveArtist(null);
        if (onSelectArtist) onSelectArtist(null);
      }
    };

    const handleTouchStart = (event) => {
      if (!event.touches?.length) return;
      const touch = event.touches[0];
      handleClick({
        clientX: touch.clientX,
        clientY: touch.clientY,
        preventDefault: () => {},
      });
    };

    applyResize();
    window.addEventListener('resize', handleResize);
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => scheduleResize());
      resizeObserver.observe(container);
    }
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i += 1) {
        const p1 = particles[i];
        for (let j = i + 1; j < particles.length; j += 1) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DISTANCE) {
            const opacity = 1 - (dist / CONNECTION_DISTANCE);
            const importanceFactor = (p1.importance + p2.importance) / 2;
            ctx.strokeStyle = `rgba(59, 130, 246, ${opacity * 0.15 * (0.5 + importanceFactor)})`;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }

      let hoveredParticle = null;
      let activeParticle = null;
      const activeSnapshot = activeArtistRef.current;

      particles.forEach((p) => {
        p.update(mouse, particles);
        if (activeSnapshot && p.artist.artist_id === activeSnapshot.artist_id) {
          activeParticle = p;
          return;
        }
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        if (Math.sqrt(dx * dx + dy * dy) < p.radius) {
          hoveredParticle = p;
        } else {
          p.draw(ctx);
        }
      });

      if (activeParticle) activeParticle.draw(ctx);
      if (hoveredParticle && hoveredParticle !== activeParticle) hoveredParticle.draw(ctx);

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('touchstart', handleTouchStart);
      if (resizeObserver) resizeObserver.disconnect();
      if (resizeRafId) cancelAnimationFrame(resizeRafId);
      cancelAnimationFrame(animationFrameId);
    };
  }, [normalizedArtists, layout.bubbleSizes, layout.maxSize, layout.minSize, getSizeBoost, onSelectArtist, hasConcertmaster]);

  const activeMeta = useMemo(() => {
    if (!activeArtist) return null;
    const isGroupAccount = activeArtist.account_type === 'group';
    const activitySource = resolvePresenceTimestamp(activeArtist);
    const last = activitySource ? new Date(activitySource) : null;
    const isOnline = Boolean(activeArtist.is_online || (last && isOnlineRecently(last)));
    const rel = isOnline ? 'オンライン' : (last ? formatRelativeTime(last) : '—');
    const title = last
      ? `${isOnline ? 'オンライン判定中（最終アクティブ）' : '最終アクティブ'}: ${last.toLocaleString()}`
      : '';
    const badgeLabel = getBadgeLabel(activeArtist, partByValue, groupTagById);
    const age = calcAge(activeArtist.birth_year, activeArtist.birth_month, activeArtist.birth_day);
    const ageLabel = isGroupAccount
      ? (activeArtist.birth_year ? `${activeArtist.birth_year}年` : '')
      : (typeof age === 'number' ? `${age}歳` : '');
    const genderLabel = renderGenderJP(activeArtist.gender);
    const email = resolveArtistEmail(activeArtist);
    return {
      rel,
      title,
      isOnline,
      badgeLabel,
      ageLabel,
      genderLabel,
      email,
    };
  }, [activeArtist, partByValue, groupTagById]);

  const activeProfile = useMemo(() => {
    if (!activeArtist) return null;
    return {
      nameEn: firstNonEmpty(activeArtist.name_en, activeArtist.artist_name_en, activeArtist.nameEn),
      partLabel: firstNonEmpty(
        activeArtist.partLabel,
        activeArtist.mainPartLabel,
        activeArtist.part,
        activeArtist.mainPart
      ),
      isChief: !!activeArtist.is_chief || !!activeArtist.isChief,
      isConcertmaster:
        !!activeArtist.is_concertmaster || !!activeArtist.isConcertmaster,
      customRoles: Array.isArray(activeArtist.custom_roles)
        ? activeArtist.custom_roles
        : Array.isArray(activeArtist.customRoles)
        ? activeArtist.customRoles
        : [],
      comment: firstNonEmpty(
        activeArtist.profile_one_liner,
        activeArtist.one_liner,
        activeArtist.comment,
        activeArtist.bio,
        activeArtist.notes
      ),
    };
  }, [activeArtist]);

  const handleOpenDetail = () => {
    if (activeArtist && onOpenDetail) onOpenDetail(activeArtist.artist_id);
  };

  const handleToggleFullscreen = async () => {
    const target = containerRef.current;
    if (!target || typeof document === 'undefined') return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (typeof target.requestFullscreen === 'function') {
        await target.requestFullscreen();
      }
    } catch {
      /* noop */
    }
  };

  const canShowListCard = showInlineCard && activeArtist && activeMeta && cardVariant !== 'profile';
  const canShowProfileCard = showInlineCard && activeArtist && cardVariant === 'profile';
  const canShowDetail = typeof onOpenDetail === 'function';
  const profileSnsItems = [
    SNS_ITEMS[1],
    SNS_ITEMS[0],
    SNS_ITEMS[2],
    SNS_ITEMS[4],
  ].filter(Boolean);

  return (
    <div
      className={`artist-bubble-view${isFullscreen ? ' is-fullscreen' : ''}`}
      ref={containerRef}
      style={resolvedContainerStyle}
    >
      {isFullscreen && <div className="artist-bubble-view__watermark" aria-hidden="true" />}
      <canvas ref={canvasRef} className="artist-bubble-view__canvas" />
      {canFullscreen && (
        <button
          type="button"
          className={`artist-bubble-view__fullscreen-btn${isFullscreen && hideFullscreenControl ? ' is-hidden' : ''}`}
          onClick={handleToggleFullscreen}
          aria-label={isFullscreen ? '全画面を閉じる' : '全画面で表示'}
          title={isFullscreen ? '全画面を閉じる' : '全画面で表示'}
        >
          <i className={`fa-solid ${isFullscreen ? 'fa-xmark' : 'fa-expand'}`} aria-hidden="true"></i>
        </button>
      )}
      {artists.length === 0 && (
        <div className="artist-bubble-view__empty" role="status">
          該当するアーティストはいません。
        </div>
      )}
      {canShowListCard && (
        <div className="artist-bubble-card" role="region" aria-label="アーティスト情報">
          <button
            type="button"
            className="artist-bubble-card__close"
            onClick={() => setActiveArtist(null)}
            aria-label="閉じる"
          >
            ×
          </button>
          <PersonAvatar
            role="artist"
            gender={activeArtist.gender}
            name={activeArtist.name}
            profilePicture={activeArtist.profile_picture}
            variant="medium"
            className="artist-bubble-card__avatar"
            initialClassName="artist-bubble-card__initial"
            style={{ backgroundColor: fallbackColorFor(activeArtist) }}
            size={58}
          />
          <div className="artist-bubble-card__info">
            <div className="artist-bubble-card__name">
              <span>{activeArtist.name || '—'}</span>
              {activeArtist.statusLabel ? (
                <span className="artist-bubble-card__status">{activeArtist.statusLabel}</span>
              ) : null}
            </div>
            <div className="artist-bubble-card__meta">
              <span
                className={`artist-bubble-card__meta-item${activeMeta.isOnline ? ' is-online' : ''}`}
                title={activeMeta.title}
              >
                {activeMeta.rel}
              </span>
              {activeMeta.genderLabel ? (
                <span className="artist-bubble-card__meta-item">{activeMeta.genderLabel}</span>
              ) : null}
              {activeMeta.ageLabel ? (
                <span className="artist-bubble-card__meta-item">{activeMeta.ageLabel}</span>
              ) : null}
              <ArtistPartnerBadge variant="icon" enabled={activeArtist.is_partner} />
              <ArtistCareerTimelineIcon artist={activeArtist} />
              <ArtistAiAnalysisBadge artist={activeArtist} />
            </div>
            {activeMeta.badgeLabel ? (
              <div className="artist-bubble-card__badge">{activeMeta.badgeLabel}</div>
            ) : null}
            {activeMeta.email ? (
              <div className="artist-bubble-card__email">{activeMeta.email}</div>
            ) : null}
            {activeArtist.is_registered ? <SnsIcons artist={activeArtist} /> : null}
          </div>
          {canShowDetail ? (
            <button type="button" className="artist-bubble-card__detail" onClick={handleOpenDetail}>
              詳細へ
            </button>
          ) : null}
        </div>
      )}
      {canShowProfileCard && activeProfile && (
        <div className="artist-bubble-card artist-bubble-card--profile" role="region" aria-label="アーティスト情報">
          <button
            type="button"
            className="artist-bubble-card__close"
            onClick={() => setActiveArtist(null)}
            aria-label="閉じる"
          >
            ×
          </button>
          <PersonAvatar
            role="artist"
            gender={activeArtist.gender}
            name={activeArtist.name}
            profilePicture={activeArtist.profile_picture}
            variant="medium"
            className="artist-bubble-card__avatar"
            initialClassName="artist-bubble-card__initial"
            style={{ backgroundColor: fallbackColorFor(activeArtist) }}
            size={58}
          />
          <div className="artist-bubble-card__info">
            <div className="artist-bubble-card__name">
              <span>{activeArtist.name || '—'}</span>
            </div>
            {activeProfile.nameEn ? (
              <div className="artist-bubble-card__name-en">{activeProfile.nameEn}</div>
            ) : null}
            {(activeProfile.partLabel ||
              activeProfile.isChief ||
              activeProfile.isConcertmaster ||
              (Array.isArray(activeProfile.customRoles) &&
                activeProfile.customRoles.length)) ? (
              <div className="artist-bubble-card__badges">
                {activeProfile.partLabel ? (
                  <span className="artist-bubble-card__badge">{activeProfile.partLabel}</span>
                ) : null}
                {activeProfile.isConcertmaster ? (
                  <span className="artist-bubble-card__badge artist-bubble-card__badge--concertmaster">
                    コンサートマスター
                  </span>
                ) : null}
                {activeProfile.isChief ? (
                  <span className="artist-bubble-card__badge artist-bubble-card__badge--chief">首席</span>
                ) : null}
                {Array.isArray(activeProfile.customRoles)
                  ? activeProfile.customRoles.map((role) => (
                      <span
                        key={`${role?.id || role?.name || 'role'}-bubble`}
                        className="artist-bubble-card__badge artist-bubble-card__badge--role"
                      >
                        {role?.name || role?.role_name || '役職'}
                      </span>
                    ))
                  : null}
              </div>
            ) : null}
            <SnsIcons artist={activeArtist} items={profileSnsItems} />
            {activeProfile.comment ? (
              <div className="artist-bubble-card__comment">{activeProfile.comment}</div>
            ) : null}
          </div>
          {canShowDetail ? (
            <button type="button" className="artist-bubble-card__detail" onClick={handleOpenDetail}>
              プロフィールページ
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ArtistBubbleView;
